<template>
  <div>
    <v-dialog v-model="show" max-width="500">
      <v-card>
        <v-card-title class="headline">{{
          vendor ? $t('components.dialog.login.loginCompany') : $t('components.dialog.login.login')
        }}</v-card-title>

        <v-card-text>
          <v-text-field v-model="model.email" label="E-postadress"></v-text-field>
          <v-text-field v-model="model.password" type="password" @keyup.enter="login" label="Lösenord"></v-text-field>

          <p class="text-error" v-if="showLoginFailedError">{{ $t('components.dialog.login.loginFailedMessage1') }}</p>
          <p v-if="showLoginFailedError" v-html="$t('components.dialog.login.loginFailedMessage2')"></p>

          <div class="mt-1">
            <a @click="openRequestResetPasswordDialog()">{{ $t('components.dialog.login.forgotYourPassword') }}</a>
          </div>
          <div class="mt-2">
            <a @click="openSignupDialog()">{{ $t('components.dialog.login.noAccountLinkText') }}</a>
          </div>
        </v-card-text>

        <v-card-actions class="pr-4 pb-3">
          <v-spacer></v-spacer>

          <v-btn text :disabled="loggingIn" @click="cancel()"> {{ $t('common.cancel') }} </v-btn>

          <v-btn color="primary" class="black--text" :disabled="loggingIn" :loading="loggingIn" @click="login()">
            {{ $t('components.dialog.login.login') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <signup-dialog-component
      :visible="showSignupDialog"
      @success="signupSuccess"
      @close="signupClosed()"
    ></signup-dialog-component>

    <request-reset-password-dialog-component
      :visible="showRequestResetPasswordDialog"
      :vendor="vendor"
      @close="requestResetPasswordClosed()"
    ></request-reset-password-dialog-component>

    <company-select-dialog :visible="showCompanySelectDialog" @close="companySelectClosed()"></company-select-dialog>

    <accept-license-dialog-component
      :visible="showAcceptLicenseDialog"
      :vendor="acceptLicenseVendor"
      :afterAcceptPromiseResolve="afterAcceptPromiseResolve"
      @close="showAcceptLicenseDialog = false"
    ></accept-license-dialog-component>
  </div>
</template>

<script>
import SignupDialogComponent from './SignupDialogComponent.vue';
import SignupVendorDialogComponent from './SignupVendorDialogComponent.vue';
import RequestResetPasswordDialogComponent from './RequestResetPasswordDialogComponent.vue';
import CompanySelectDialog from './CompanySelectDialogComponent.vue';
import AcceptLicenseDialogComponent from './AcceptLicenseDialogComponent.vue';
import { mapStores } from 'pinia';
import { useUserStore } from '../../stores/UserStore';

export default {
  components: {
    SignupDialogComponent,
    SignupVendorDialogComponent,
    RequestResetPasswordDialogComponent,
    CompanySelectDialog,
    AcceptLicenseDialogComponent,
  },

  props: {
    visible: Boolean,
    vendor: {
      type: Boolean,
      default: false,
    },
    download: {
      type: Boolean,
      default: false,
    },
  },

  model: {
    prop: 'visible',
    event: 'update',
  },

  data() {
    return {
      model: {},
      companies: [],
      showSignupDialog: false,
      showRequestResetPasswordDialog: false,
      showLoginFailedError: false,
      showCompanySelectDialog: false,
      showAcceptLicenseDialog: false,
      afterAcceptPromiseResolve: undefined,
      acceptLicenseVendor: false,
      loggingIn: false,
    };
  },

  computed: {
    ...mapStores(useUserStore),

    show: {
      get: function () {
        return this.visible;
      },
      set: function (value) {
        this.$emit('update', value);

        if (value === false) {
          this.$emit('close');
        }
      },
    },
  },

  methods: {
    async login() {
      this.showLoginFailedError = false;
      this.loggingIn = true;

      await this.userStore.signIn(this.model);

      if (!this.userStore.isAuthenticated) {
        this.showLoginFailedError = true;
        this.loggingIn = false;
        return;
      }

      if (this.userStore.user.accepted_license === 0) {
        await new Promise((resolve, reject) => {
          this.acceptLicenseVendor = this.userStore.isAuthenticatedVendor;
          this.showAcceptLicenseDialog = true;
          this.afterAcceptPromiseResolve = () => resolve(this.userStore.user);
        });
      }

      if (!this.userStore.isAuthenticatedVendor) {
        this.cancel();
        this.$emit('success', { vendor: false });
        return;
      }

      if (!this.userStore.hasSelectedCompany) {
        this.openCompanySelectDialog();
        return;
      }

      this.cancel();
      this.$emit('success', { vendor: true, gotoMyProfile: true });
    },

    cancel() {
      this.clearFields();
      this.show = false;
    },

    clearFields() {
      this.model.email = '';
      this.model.password = '';
      this.loggingIn = false;
    },

    openSignupDialog() {
      this.clearFields();
      this.show = false;
      if (this.vendor) {
        this.$router.push({ name: 'VendorPrice' }).catch(this.navigationErrorHandler);
      } else {
        this.showSignupDialog = true;
      }
    },

    openRequestResetPasswordDialog() {
      this.clearFields();
      this.show = false;
      this.showRequestResetPasswordDialog = true;
    },

    openCompanySelectDialog() {
      this.clearFields();
      this.show = false;
      this.showCompanySelectDialog = true;
    },

    signupClosed() {
      this.showSignupDialog = false;
    },

    requestResetPasswordClosed() {
      this.showRequestResetPasswordDialog = false;
    },

    companySelectClosed() {
      this.showCompanySelectDialog = false;

      this.$emit('success', { vendor: true, gotoMyProfile: !this.download });
    },

    signupSuccess() {
      this.$emit('success', { vendor: false });
    },
  },
};
</script>
